/* eslint no-console: ["error", { allow: ["warn", "error"] }] */

import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import isEqual from 'lodash/isEqual';
import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensors,
  useSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';

import { setPhotoCollectionSortOrder } from '@redux/modules/photo';
import { selectValidPhotos } from '@redux/selectors/PhotoSelector';

import SortableItem from './components/SortableItem';
import Photo from './components/Photo';
import DroppableContainer from './components/DroppableContainer';

const DROPPED_ITEM_TIMEOUT = 2000;
const defaultContainerStyle = ({ isOverContainer }) => ({
  marginTop: 40,
  backgroundColor: isOverContainer
    ? 'rgb(235,235,235,1)'
    : 'rgba(246,246,246,1)',
});

export const VOID_ID = 'void';

export default function GridList({
  adjustScale = false,
  collisionDetection = closestCorners,
  columns,
  files,
  setFiles,
  handle = false,
  ulStyle = {},
  getItemStyles = () => ({}),
  getContainerStyle = defaultContainerStyle,
  wrapperStyle = () => ({}),
  renderItem,
  strategy = rectSortingStrategy,
}) {
  const dispatch = useDispatch();
  const propagateItems = useRef(false);
  const validPhotos = useSelector(selectValidPhotos); // drives creation of photo cards.

  const [items, setItems] = useState([]); // loaded from files prop
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedItemsDict, setSelectedItemsDict] = useState({});
  const [droppedItems, setDroppedItems] = useState([]);
  const [dragOverlaydItems, setClonedItems] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const updateCollectionSortOrder = orderedArray => dispatch(setPhotoCollectionSortOrder(orderedArray));

  useEffect(() => {
    if (validPhotos) {
      const displayKeys = validPhotos.map(dph => dph.key);
      const filesKeys = files.map(fileRef => fileRef.key);
      if (displayKeys !== filesKeys) setFiles(validPhotos);
    }
  }, [validPhotos]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 15,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const findContainer = id => {
    if (id in items) {
      return id;
    }

    return Object.keys(items).find(key => items[key].find(item => item.id === id));
  };

  const getIndex = id => {
    const container = findContainer(id);

    if (!container) {
      return -1;
    }

    const index = items[container].findIndex(item => item.id === id);

    return index;
  };

  useEffect(() => {
    if (files && !isEqual(items.A, files)) {
      files.sort((f1, f2) => f1.SortOrder - f2.SortOrder); // arranges according to sort order, ascending
      if (files.length < items.A?.length) updateCollectionSortOrder(files.map(f => f.key));
      setItems({ A: files, [VOID_ID]: [] });
    }
  }, [files]);

  useEffect(() => {
    if (items && !isEqual(items.A, files)) {
      if (propagateItems.current) {
        setFiles(items.A);
        propagateItems.current = false;
      }
    }
  }, [items]);

  const onSelectionChanged = (item, isCtrlClicked) => {
    if (isCtrlClicked) {
      if (selectedItemsDict[item.id]) {
        setSelectedItems(selectedItems.filter(slctItem => (slctItem.id === item.id)));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
      setSelectedItemsDict({ ...selectedItemsDict, [item.id]: true });
    } else {
      setSelectedItems([]);
    }
  };
  /*
    Commented out because it kept throwing an error when one of the ComboBoxes
      in FormikInputGroup.jsx is set to stay open on select. 9/15/21
  */
  useEffect(() => {
    const clearSelection = ({ target }) => {
      try {
        if (target?.nodeName !== 'LI' && target?.parentNode?.nodeName !== 'LI') {
          setSelectedItems([]);
          setSelectedItemsDict([]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    document.addEventListener('click', clearSelection);

    return () => {
      document.removeEventListener('click', clearSelection);
    };
  }, []);

  useEffect(() => {
    let timeout = 0;
    if (droppedItems.length) {
      timeout = window.setTimeout(
        () => setDroppedItems([]),
        DROPPED_ITEM_TIMEOUT,
      );
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [droppedItems.length]);

  const handleDragStart = event => {
    const { active } = event;
    setActiveId(active.id);
    setClonedItems(items);
    if (selectedItemsDict[active.id]) {
      setSelectedItems([]);
    } else if (selectedItems.length > 0) {
      setItems(
        selectedItems.reduce((ret, selectedItem) => {
          if (selectedItem.id === active.id) {
            return ret;
          }
          const container = findContainer(selectedItem.id);
          return !container
            ? ret
            : {
              ...ret,
              [container]: ret[container].filter(
                item => item.id !== selectedItem.id,
              ),
            };
        }, items),
      );
      propagateItems.current = true;
    }
  };

  const handleDragOver = event => {
    const { active, over, draggingRect } = event;
    const overId = over?.id || VOID_ID;
    const overContainer = findContainer(overId);
    const activeContainer = findContainer(active.id);

    if (!overContainer || !activeContainer) {
      return;
    }

    if (activeContainer !== overContainer) {
      const activeItems = items[activeContainer];
      const overItems = items[overContainer];
      const overIndex = overItems.findIndex(item => item.id === overId);
      const activeIndex = activeItems.findIndex(item => item.id === active.id);

      let newIndex;
      if (overId in items) {
        newIndex = overItems.length + 1;
      } else {
        const isBelowLastItem = over
          && overIndex === overItems.length - 1
          && draggingRect.offsetTop > over.rect.offsetTop + over.rect.height;

        const modifier = isBelowLastItem ? 1 : 0;

        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
      }
      setItems({
        ...items,
        [activeContainer]: [
          ...items[activeContainer].filter(item => item.id !== active.id),
        ],
        [overContainer]: [
          ...items[overContainer].slice(0, newIndex),
          items[activeContainer][activeIndex],
          ...items[overContainer].slice(
            newIndex,
            items[overContainer].length,
          ),
        ],
      });
      propagateItems.current = true;
    }
  };

  const handleDragEnd = event => {
    const { active, over } = event;
    const activeContainer = findContainer(active.id);

    if (!activeContainer) {
      setActiveId(null);
      return;
    }

    const overId = over?.id || '';

    if (overId === VOID_ID) {
      setItems({
        ...(over?.id === VOID_ID ? items : dragOverlaydItems),
        [VOID_ID]: [],
      });
      propagateItems.current = true;
      setActiveId(null);
      return;
    }

    const overContainer = findContainer(overId);

    if (activeContainer && overContainer) {
      const activeIndex = items[activeContainer].findIndex(item => item.id === active.id);
      const overIndex = items[overContainer].findIndex(item => item.id === overId);
      if (selectedItems.length) {
        const newItems = { ...items };
        newItems[overContainer] = arrayMove(
          newItems[overContainer],
          activeIndex,
          overIndex,
        );
        newItems[overContainer].splice(
          overIndex + 1,
          0,
          ...selectedItems.filter(item => item.id !== active.id),
        );
        setItems(newItems);
        propagateItems.current = true;
        setDroppedItems(selectedItems);
        setSelectedItems([]);
      } else if (activeIndex !== overIndex) {
        setItems({
          ...items,
          [overContainer]: arrayMove(
            items[overContainer],
            activeIndex,
            overIndex,
          ),
        });
        propagateItems.current = true;
      }
    }

    const newOrder = over.data.current.sortable.items;
    updateCollectionSortOrder(newOrder);
    setActiveId(null);
  };

  const handleDragCancel = () => {
    if (dragOverlaydItems) {
      // Reset items to their original state in case items have been
      // Dragged across containrs
      setItems(dragOverlaydItems);
      propagateItems.current = true;
    }

    setActiveId(null);
    setClonedItems(null);
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      {items && (
        Object.keys(items)
          .filter(key => key !== VOID_ID)
          .map(containerId => (
            <SortableContext
              key={containerId}
              items={items[containerId]}
              strategy={strategy}
            >
              <DroppableContainer
                ulStyle={ulStyle}
                id={containerId}
                columns={columns}
                items={items[containerId]}
                getStyle={getContainerStyle}
              >
                {items[containerId].map((item, index) => (
                  <SortableItem
                    item={item}
                    isSelected={selectedItemsDict[item.id]}
                    key={item.key}
                    id={item.id}
                    onSelect={onSelectionChanged}
                    isDropped={droppedItems.findIndex(drop => drop.id === item.id) !== -1}
                    index={index}
                    handle={handle}
                    style={getItemStyles}
                    wrapperStyle={wrapperStyle}
                    // onEditClicked={onEditClicked}
                    // onRemove={onRemove}
                    renderItem={renderItem}
                    containerId={containerId}
                    getIndex={getIndex}
                  />
                ))}
              </DroppableContainer>
            </SortableContext>
          )))}
      {createPortal(
        <DragOverlay // holder element when drag n dropping
          adjustScale={adjustScale}
          className="dragOverlayContainer"
          style={{}}
        >
          {activeId ? (
            <>
              <Photo
                item={items.A.find(item => item.id === activeId)}
                value={activeId}
                handle={handle}
                className={selectedItems.length > 0 ? 'selectedItem' : ''}
                style={getItemStyles({
                  containerId: findContainer(activeId),
                  overIndex: -1,
                  index: getIndex(activeId),
                  value: activeId,
                  isSorting: activeId !== null,
                  isDragging: true,
                  isDragOverlay: true,
                })}
                // onEditClicked={onEditClicked}
                // onRemove={onRemove}
                wrapperStyle={wrapperStyle({ index: 0 })}
                renderItem={renderItem}
                dragOverlay
              />
              {selectedItems
                .filter(itemId => itemId !== activeId)
                .slice(0, 4)
                .map(item => (
                  <Photo
                    item={item}
                    key={item.key}
                    value={item.id}
                    className="selectedItem"
                    handle={handle}
                    style={getItemStyles({
                      containerId: findContainer(item.id),
                      overIndex: -1,
                      index: getIndex(item.id),
                      value: item.id,
                      isSorting: item !== null,
                      isDragging: true,
                      isDragOverlay: true,
                    })}
                    // onEditClicked={onEditClicked}
                    // onRemove={onRemove}
                    wrapperStyle={wrapperStyle({ index: 0 })}
                    renderItem={renderItem}
                    dragOverlay
                  />
                ))}
              {selectedItems.length > 5 && (
                <div className="badge">
                  <span>
                    {selectedItems.length}
                    items
                  </span>
                </div>
              )}
            </>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}

GridList.propTypes = {
  columns: PropTypes.number,
  files: PropTypes.arrayOf(PropTypes.shape()),
  setFiles: PropTypes.func,
  adjustScale: PropTypes.bool,
  collisionDetection: PropTypes.string,
  handle: PropTypes.bool,
  ulStyle: PropTypes.shape(),
  // onRemove: PropTypes.func,
  // onEditClicked: PropTypes.func,
  getItemStyles: PropTypes.func,
  getContainerStyle: PropTypes.arrayOf(PropTypes.string),
  wrapperStyle: PropTypes.func,
  renderItem: PropTypes.arrayOf(PropTypes.string, PropTypes.shape()),
  strategy: PropTypes.arrayOf(PropTypes.string, PropTypes.shape()),
};
